import { useUser } from '@/hooks/useUser';
import { useFlag } from '@simplywallst/unleash';
import { useEffect, useRef } from 'react';
import { generateSHA1Hash, useImpactSessionID, useImRef } from './utils';

const SDK_JS_URL = 'https://simplywall.st/static/vendor/impact-20250819.js';
const SDK_SCRIPT = `(function(a,b,c,d,e,f,g){e['ire_o']=c;e[c]=e[c]||function(){(e[c].a=e[c].a||[]).push(arguments)};f=d.createElement(b);g=d.getElementsByTagName(b)[0];f.async=1;f.src=a;g.parentNode.insertBefore(f,g);})('${SDK_JS_URL}','script','ire',document,window);`;

/**
 * Initializes the impact SDK.
 */
export const useImpactInit = () => {
  /**
   * Whether the SDK has been loaded + initialized.
   */
  const hasInitRef = useRef(false);
  const flag = useFlag('impact-sdk');

  const imRef = useImRef();
  const sessionID = useImpactSessionID();

  const user = useUser();

  useEffect(() => {
    const identify = async () => {
      if (hasInitRef.current && user.data?.loggedIn) {
        const emailHash = user.data?.emailAddress
          ? await generateSHA1Hash(user.data?.emailAddress)
          : undefined;

        window?.ire?.('identify', {
          customerId: user.data?.id,
          customerEmail: emailHash ?? undefined,
        });
      }
    };

    identify();
  }, [user.data?.loggedIn, user.data?.emailAddress, user.data?.id, sessionID]);

  useEffect(() => {
    if (hasInitRef.current || !flag.enabled) {
      return;
    }

    try {
      const script = document.createElement('script');
      script.innerHTML = SDK_SCRIPT;
      document.head.appendChild(script);
      hasInitRef.current = true;

      const hasImRef = imRef.get() !== undefined;

      if (!user.data?.loggedIn && hasImRef) {
        // We need to ensure script is loaded before calling identify
        setTimeout(() => {
          window?.ire?.('identify', {
            customProfileId: sessionID.get(),
          });
        }, 100);
      }
    } catch {
      // noop
    }
  }, [flag.enabled, user.data?.loggedIn, sessionID, imRef]);

  return;
};
